import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';

import { ReactHookForm } from "../../../../components/hook-form/react-hook-form.component";
import { useReactHookForm } from "../../../../hooks/useReactHookForm";
import { useSnackbar } from "../../../../hooks/use-snackbar.hook";
import { parseErrorMessageRecursive } from "../../../../utils/yup-error-parser.util";
import 'react-quill/dist/quill.snow.css';
import FormRichTextField from "../../../../components/hook-form/form-rich-textbox.component";
import FormInput from "../../../query-builder/hooks-form-querybuilder/form-input.component";
import { MutationKeyConfig } from "../../../../api/api-helper";
import { InvalidateRoute } from "../../../../hooks/useDeleteData";
import { useSaveData } from "../../../../hooks/useSaveData";
import { LoadingIndicator } from "../../../../components/loading-indicator.component";
import { municipalityApiKeyStore } from "../../../../api/masters/municipality-api-key-store";
import { FormSelectDynamic } from "../../../../components/hook-form/form-select.dynamic.component";
import { useAuthorize } from "../../../../hooks/use-authorize";
import { UserPermissionName } from "../../../../models/user-permission-name";

export const noteFormSchema = Yup.object().shape({
    topic: Yup.string().required('Topic is required'),
    note: Yup.string().required('Note is required'),
});

interface CreateNoteDialog {
    title: string;
    dataItem: any;
    config: { saveConfig: MutationKeyConfig; invalidateRoute?: InvalidateRoute };
    setNoteValue: (noteData: any) => void;
    onClosed: () => void;
}
export const CreateNoteDialog = ({
    title,
    dataItem,
    config: { saveConfig, invalidateRoute },
    setNoteValue,
    onClosed
}: CreateNoteDialog) => {

    const { showSnackbar } = useSnackbar();
    const form = useReactHookForm({
        defaultValues: { ...dataItem },
        yupSchema: noteFormSchema,
    });

    const { isLoading, isError, error, mutateAsync } = useSaveData(saveConfig, invalidateRoute);

    const handleSubmit = async (noteData: any) => {
        const notePayload = {
            municipalityNoteMappingID: noteData?.municipalityNoteMappingID ?? 0,
            municipalityID: noteData?.municipalityID ?? 0,
            topic: noteData?.topic ?? '',
            subTitle: noteData?.subTitle ?? '',
            note: noteData?.note,
            noteTempEntityID: noteData?.noteTempEntityID ?? ''
        }
        const response = await mutateAsync({ data: notePayload });
        setNoteValue(response?.data);
        onClosed();
    };

    const handleError = (error: any) => {
        const messages = parseErrorMessageRecursive(error);
        const errorCount = messages?.length > 1 ? ` | +${messages.length - 1} more` : '';

        let requiredMessages: any[] = [];
        let otherMessages: any[] = [];

        // Classify messages
        Array.isArray(messages) &&
            messages?.forEach((message) => {
                if (message.endsWith('is required')) {
                    requiredMessages.push(message?.split(' is required')[0]?.trim());
                } else {
                    otherMessages.push(message.trim());
                }
            });

        // Remove duplicates
        requiredMessages = Array.from(new Set(requiredMessages));
        otherMessages = Array.from(new Set(otherMessages));

        // Construct the final message
        const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
        const finalOtherMessage = otherMessages?.join(', ');

        // Combine messages
        const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

        // Show the final formatted message
        showSnackbar(`Validation Error: ${finalMessage}`, 'error');
    };

    const handleFormSubmit = form.handleSubmit(handleSubmit as any, handleError);

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <Dialog
            open={true}
            fullWidth={true}
            maxWidth={'md'}
        //  onClose={onClosed}
        >
            <DialogTitle id="form-dialog-title">{title}</DialogTitle>
            <IconButton

                aria-label="close"
                onClick={onClosed}
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 15,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Tooltip title={<Typography variant="body1">Close</Typography>}>
                    <CloseIcon />
                </Tooltip>
            </IconButton>
            <ReactHookForm formName="MUNICIPALITY_NOTE_FORM" onSubmit={() => { }} formContextProps={form}>
                <DialogContent dividers className="popupDilog">
                    <LoadingIndicator isLoading={isLoading} isSpinner />
                    <Grid container spacing={2}>
                        <Grid item xl={12} lg={12} sm={12} xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xl={6} lg={6} sm={6} xs={6}>
                                    <FormSelectDynamic
                                        name="topic"
                                        label="Topic"
                                        getQueryKeyConfig={() => municipalityApiKeyStore.getMunicipalityNoteTopics()}
                                        optionsValueKey="topicValue"
                                        optionsLabelKeys={['topicText']}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xl={12} lg={12} sm={12} xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xl={6} lg={6} sm={6} xs={6}>
                                    <FormInput
                                        label="Subtitle (Optional)"
                                        name="subTitle"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xl={12} lg={12} sm={12} xs={12}>
                            <FormRichTextField name='note' placeHolder='Write your note here...' />
                        </Grid>
                    </Grid>
                </DialogContent>
                <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
                    <DialogActions className="spaceBetween">
                        <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                            Cancel
                        </Button>
                        <Button type="button" variant="contained" style={{ width: '130px' }} disabled={isLoading || isReadOnlyPermission} onClick={handleFormSubmit}>
                            Save
                        </Button>
                    </DialogActions>
                </div>
            </ReactHookForm>
        </Dialog>
    );
}